package fr.frux.shared.data.repository

import dev.zacsweers.metro.ContributesBinding
import dev.zacsweers.metro.Inject
import dev.zacsweers.metro.SingleIn
import fr.frux.core.common.di.AppScope
import fr.frux.core.common.error.AppError
import fr.frux.core.common.logging.Logger
import fr.frux.core.common.result.runCatchingCancellable
import fr.frux.core.maps.MapBounds
import fr.frux.core.network.api.SiteApi
import fr.frux.core.network.error.mapFailureToAppError
import fr.frux.shared.data.local.CachedSites
import fr.frux.shared.data.local.SiteLocalDataSource
import fr.frux.shared.data.mapper.toDomain
import fr.frux.shared.data.mapper.toDto
import fr.frux.shared.data.mapper.toRequest
import fr.frux.shared.data.repository.TreeRepositoryImpl.Companion.toBboxQuery
import fr.frux.shared.di.IoDispatcher
import fr.frux.shared.domain.model.NearbySites
import fr.frux.shared.domain.model.PlantingDraft
import fr.frux.shared.domain.model.Site
import fr.frux.shared.domain.model.SiteAccessRule
import fr.frux.shared.domain.model.SiteDraft
import fr.frux.shared.domain.model.SiteReportKind
import fr.frux.shared.domain.model.allowsContribution
import fr.frux.shared.domain.repository.SiteRepository
import fr.frux.shared.dto.CreateSiteReportRequest
import fr.frux.shared.dto.UpdateSiteRequest
import kotlinx.coroutines.CoroutineDispatcher
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.flowOn
import kotlinx.coroutines.withContext

/**
 * Lieux de cueillette (ADR 0035). Les lectures (`getNearbySites`, `getSite`) sont
 * stale-while-revalidate via [SiteLocalDataSource], sur le patron de `TreeRepositoryImpl` : cache
 * émis d'abord, réseau ensuite, cache réécrit sur succès seul et laissé intact sur échec. Le reste
 * est réseau seul. Chaque écriture réussie publie l'id du lieu sur
 * [changes] pour que la carte et la fiche se rafraîchissent sans se connaître.
 */
@ContributesBinding(AppScope::class)
@SingleIn(AppScope::class)
@Inject
class SiteRepositoryImpl(
    private val siteApi: SiteApi,
    @IoDispatcher private val dispatcher: CoroutineDispatcher,
    private val localDataSource: SiteLocalDataSource,
) : SiteRepository {
    private val _changes = MutableSharedFlow<Long>(extraBufferCapacity = 8)
    override val changes: SharedFlow<Long> = _changes.asSharedFlow()

    private val _removals = MutableSharedFlow<Long>(extraBufferCapacity = 8)
    override val removals: SharedFlow<Long> = _removals.asSharedFlow()

    override fun getNearbySites(
        lat: Double,
        lon: Double,
        radiusKm: Double,
        bounds: MapBounds?,
        limit: Int,
    ): Flow<Result<NearbySites>> =
        flow {
            // Une lecture cache qui casse ne doit jamais coûter le parcours réseau.
            val cached =
                runCatchingCancellable { localDataSource.nearbySites(lat, lon, radiusKm) }
                    .onFailure { Logger.e(TAG, "⚠️ Lecture du cache lieux échouée", it) }
                    .getOrDefault(CachedSites(sites = emptyList(), isStale = false))
            if (cached.sites.isNotEmpty()) {
                Logger.d(TAG, "💾 ${cached.sites.size} lieux servis depuis le cache local")
                emit(Result.success(NearbySites(sites = cached.sites.take(limit), isStale = cached.isStale)))
            }
            emit(
                runCatchingCancellable {
                    // Pas de lat/lon dans les logs : position utilisateur = donnée personnelle.
                    val sites =
                        siteApi
                            .getNearbySites(lat, lon, radiusKm, limit, bounds?.toBboxQuery())
                            .sites
                            .map { it.toDomain() }
                    cacheSites(sites)
                    NearbySites(sites = sites)
                }.onFailure { Logger.e(TAG, "❌ Lieux alentour échoués", it) }
                    .mapFailureToAppError(),
            )
        }.flowOn(dispatcher)

    override fun getSite(id: Long): Flow<Result<Site>> =
        flow {
            val cached =
                runCatchingCancellable { localDataSource.site(id) }
                    .onFailure { Logger.e(TAG, "⚠️ Lecture du cache du lieu $id échouée", it) }
                    .getOrNull()
            if (cached != null) emit(Result.success(cached))
            val result = fetchSite(id)
            // Un cache déjà servi tient lieu de réponse face à une panne, jamais face à un lieu
            // supprimé ou masqué : la copie locale contournerait sinon la modération.
            if (cached == null || result.isSuccess || result.isNotFound()) emit(result)
        }.flowOn(dispatcher)

    override suspend fun refreshSite(id: Long): Result<Site> = withContext(dispatcher) { fetchSite(id) }

    override suspend fun nearestSite(
        lat: Double,
        lon: Double,
        withinMeters: Double,
    ): Result<Site?> =
        withContext(dispatcher) {
            runCatchingCancellable {
                // Le serveur trie par distance croissante (KNN) et sert isOwner sur la liste (#651) :
                // on saute les lieux privés d'autrui, qui refuseraient le rattachement.
                siteApi
                    .getNearbySites(lat, lon, withinMeters / METERS_PER_KM, limit = NEAREST_CANDIDATES)
                    .sites
                    .map { it.toDomain() }
                    .firstOrNull { it.allowsContribution() }
            }.onFailure { Logger.e(TAG, "❌ Recherche du lieu le plus proche échouée", it) }
                .mapFailureToAppError()
        }

    override suspend fun createSite(
        draft: SiteDraft,
        idempotencyKey: String,
    ): Result<Site> = write("création") { siteApi.createSite(draft.toRequest(), idempotencyKey).toDomain() }

    override suspend fun addPlanting(
        siteId: Long,
        planting: PlantingDraft,
    ): Result<Site> = write("ajout de plantation") { siteApi.addPlanting(siteId, planting.toRequest()).toDomain() }

    override suspend fun attachTree(
        siteId: Long,
        treeId: Long,
    ): Result<Site> = write("rattachement") { siteApi.attachTree(siteId, treeId).toDomain() }

    override suspend fun detachTree(
        siteId: Long,
        treeId: Long,
    ): Result<Site> = write("détachement") { siteApi.detachTree(siteId, treeId).toDomain() }

    override suspend fun claim(id: Long): Result<Site> = write("revendication") { siteApi.claimSite(id).toDomain() }

    override suspend fun unclaim(id: Long): Result<Site> = write("libération") { siteApi.unclaimSite(id).toDomain() }

    override suspend fun setAccessRule(
        id: Long,
        rule: SiteAccessRule,
    ): Result<Site> =
        write("changement de règle d'accès") {
            siteApi.updateSite(id, UpdateSiteRequest(accessRule = rule.toDto())).toDomain()
        }

    override suspend fun setFavorite(
        id: Long,
        favorite: Boolean,
    ): Result<Unit> =
        withContext(dispatcher) {
            runCatchingCancellable {
                if (favorite) siteApi.favoriteSite(id) else siteApi.unfavoriteSite(id)
            }.onSuccess { _changes.tryEmit(id) }
                .onFailure { Logger.e(TAG, "❌ Favori du lieu $id échoué", it) }
                .mapFailureToAppError()
        }

    override suspend fun favoriteSites(): Result<List<Site>> =
        withContext(dispatcher) {
            runCatchingCancellable { siteApi.getFavoriteSites().sites.map { it.toDomain() } }
                .onFailure { Logger.e(TAG, "❌ Lieux favoris échoués", it) }
                .mapFailureToAppError()
        }

    override suspend fun reportSite(
        id: Long,
        kind: SiteReportKind,
        details: String?,
    ): Result<Site> =
        write("signalement") {
            siteApi.reportSite(id, CreateSiteReportRequest(kind = kind.name, details = details)).toDomain()
        }

    override suspend fun deleteSite(id: Long): Result<Unit> =
        withContext(dispatcher) {
            runCatchingCancellable { siteApi.deleteSite(id) }
                .onSuccess {
                    runCatchingCancellable { localDataSource.delete(id) }
                        .onFailure { Logger.e(TAG, "⚠️ Retrait du lieu $id du cache échoué", it) }
                    _changes.tryEmit(id)
                }.onFailure { Logger.e(TAG, "❌ Suppression du lieu $id échouée", it) }
                .mapFailureToAppError()
        }

    /** Réécriture sur succès réseau seul. Les échecs de cache restent isolés du lot frais. */
    private suspend fun cacheSites(sites: List<Site>) {
        runCatchingCancellable { localDataSource.upsertAll(sites) }
            .onSuccess {
                runCatchingCancellable { localDataSource.prune() }
                    .onFailure { Logger.e(TAG, "⚠️ Nettoyage du cache lieux échoué", it) }
            }.onFailure { Logger.e(TAG, "⚠️ Écriture du cache lieux échouée", it) }
    }

    /** Réseau seul. Un 404 retire la copie locale : le lieu n'existe plus ou a été masqué. */
    private suspend fun fetchSite(id: Long): Result<Site> =
        runCatchingCancellable { siteApi.getSite(id).toDomain().also { cacheSites(listOf(it)) } }
            .mapFailureToAppError()
            .onFailure { error ->
                if (error is AppError.NotFound) {
                    // Attendu quand un lieu disparaît ailleurs : pas une erreur.
                    Logger.i(TAG, "Lieu $id introuvable, retiré du cache")
                    runCatchingCancellable { localDataSource.delete(id) }
                        .onFailure { Logger.e(TAG, "⚠️ Retrait du lieu $id du cache échoué", it) }
                    _removals.tryEmit(id)
                } else {
                    Logger.e(TAG, "❌ Lecture du lieu $id échouée", error)
                }
            }

    private fun Result<Site>.isNotFound(): Boolean = exceptionOrNull() is AppError.NotFound

    /**
     * Le lieu rendu par le serveur est posé dans le cache avant l'annonce sur [changes] : les
     * rechargements qu'elle déclenche serviraient sinon une copie d'avant le geste (lieu neuf absent,
     * ancien effectif).
     */
    private suspend fun write(
        label: String,
        block: suspend () -> Site,
    ): Result<Site> =
        withContext(dispatcher) {
            runCatchingCancellable { block() }
                .onSuccess { site ->
                    cacheSites(listOf(site))
                    _changes.tryEmit(site.id)
                }.onFailure { Logger.e(TAG, "❌ Lieu : $label échoué", it) }
                .mapFailureToAppError()
        }

    private companion object {
        const val TAG = "SiteRepository"
        const val METERS_PER_KM = 1000.0
        const val NEAREST_CANDIDATES = 5
    }
}
